import { AxiosResponse } from 'axios'
import { useState } from 'react'
import { HttpClient } from '@opallabs/api'
import { OpalWordmark } from '@opallabs/beacon'
import { useTheme } from '@opallabs/hooks'
import {
  Button,
  Link,
  ParagraphText,
  SectionHeading,
  HeadingSize,
} from '@opallabs/lapidary'
import type { WithTheme } from '@opallabs/lapidary'
import { styles } from './styles'
import { UserSegmentCard } from './UserSegmentCard'

type UserSegmentationProps = Readonly<
  WithTheme & {
    givenName: string
    userId: string
  }
>

export type SuccessResponse = Readonly<
  JSON & {
    destination: string
  }
>

export type Role = {
  glyph: string
  description: string
  name: string
}

export const segmentRoles: Role[] = [
  {
    glyph: 'gem-stories',
    description: 'I create strategic plans or campaigns',
    name: 'strategist',
  },
  {
    glyph: 'gem-presentations',
    description: 'I manage a brand, channel, or tactic',
    name: 'manager',
  },
  {
    glyph: 'gem-edit',
    description: 'I develop creative or copy',
    name: 'creative',
  },
  {
    glyph: 'gem-users',
    description: 'I coordinate marketing campaigns',
    name: 'project_manager',
  },
  {
    glyph: 'gem-thumbs-up',
    description: 'I review or approve content',
    name: 'approver',
  },
  {
    glyph: 'gem-calendar',
    description: 'I schedule content',
    name: 'scheduler',
  },
]

export const UserSegmentationModal: React.VFC<UserSegmentationProps> = ({
  givenName,
}) => {
  const theme = useTheme()

  const [selectedRoles, setSelectedRoles] = useState<Array<string>>([])
  const onSelect = (name: string) => {
    if (selectedRoles.length === 2) {
      if (selectedRoles.indexOf(name) === 1) {
        return setSelectedRoles([selectedRoles[0]])
      }

      return setSelectedRoles([selectedRoles[1]])
    }

    if (selectedRoles.includes(name)) {
      return setSelectedRoles([])
    }

    return setSelectedRoles([...selectedRoles, name])
  }

  async function onDone() {
    try {
      const response: AxiosResponse<SuccessResponse> = await HttpClient().post(
        '/personalize',
        { data: { segments: selectedRoles } }
      )

      window.location.assign(response.data.destination)
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <section css={styles.sectionStyles({ theme })}>
      <OpalWordmark
        includeLogo={true}
        styleOverrides={{ icon: styles.iconOverrides({ theme }) }}
        {...{
          theme,
        }}
      />
      <SectionHeading
        size={HeadingSize.large}
        overrideStyles={styles.headingStyles}
      >
        Welcome {givenName}, let&rsquo;s personalize your account!
      </SectionHeading>
      <fieldset>
        <legend css={styles.legend}>
          <ParagraphText
            color="Gray"
            styleOverrides={styles.paragraphStyles}
            {...{
              theme,
            }}
          >
            To help us customize your onboarding experience, select up to two
            functions you perform most in your role
          </ParagraphText>
        </legend>
        <ul css={styles.cardContainer} aria-label="Functions">
          {segmentRoles.map((role) => (
            <UserSegmentCard
              key={role.name}
              clickable={
                selectedRoles.length < 2 || selectedRoles.includes(role.name)
              }
              description={role.description}
              icon={role.glyph}
              name={role.name}
              onSelect={() => onSelect(role.name)}
              selected={selectedRoles.includes(role.name)}
              {...{
                theme,
              }}
            />
          ))}
        </ul>
      </fieldset>
      <Link
        href={window.location.origin}
        styleOverrides={styles.link}
        {...{
          theme,
        }}
      >
        None of the Above
      </Link>
      <footer css={styles.footer({ theme })}>
        <span>Personalize your account</span>
        <Button
          name="done"
          disabled={selectedRoles.length === 0}
          variant="primary"
          onClick={onDone}
          buttonText="Done"
          {...{
            theme,
          }}
        />
      </footer>
    </section>
  )
}
